import React from 'react'
import PropTypes from 'prop-types'
import { Title } from '@offerzen/pinata'

import DescriptionList, { DescriptionItem } from '@/Jobs/components/DescriptionList'
import RequirementTag from '@/Jobs/components/RequirementTag'

import styles from './styles'

const SkillsAndExperienceSection = ({ roleExperience, otherRoles, mustHaveSkillExperiences, niceToHaveSkills, languages }) => {
  return (
    <section>
      <Title className={styles.heading}>Skills and experience</Title>

      <DescriptionList>
        <DescriptionItem term="Role" details={roleExperience?.role?.name} />

        <If condition={otherRoles?.length}>
          <DescriptionItem
            term="Other roles we'll consider"
            details={
              <ul>
                <For each="role" of={otherRoles} index="index">
                  <Choose>
                    <When condition={index === otherRoles.length - 1}>
                      <li key={role.id}>{role.name}</li>
                    </When>
                    <Otherwise>
                      <li key={role.id}>{role.name},</li>
                    </Otherwise>
                  </Choose>
                </For>
              </ul>
            }
          />
        </If>

        <DescriptionItem
          term="Experience in role"
          details={
            <Choose>
              <When condition={roleExperience?.yearsExperience === 0}>No experience required</When>
              <When condition={roleExperience?.yearsExperience}>{roleExperience.yearsExperience}+ years</When>
              <Otherwise />
            </Choose>
          }
        />

        <DescriptionItem
          term="Must-have skills"
          details={
            <If condition={mustHaveSkillExperiences?.length}>
              <ul>
                <For each="skillExperience" of={mustHaveSkillExperiences}>
                  <li key={skillExperience.id}>
                    <RequirementTag
                      text={`${skillExperience.skill.name}${
                        skillExperience?.yearsExperience ? ` (${skillExperience.yearsExperience} years exp.)` : ''
                      }`}
                    />
                  </li>
                </For>
              </ul>
            </If>
          }
        />

        <If condition={niceToHaveSkills?.length}>
          <DescriptionItem
            term="Nice-to-have skills"
            details={
              <ul>
                <For each="skill" of={niceToHaveSkills}>
                  <li key={skill.id}>
                    <RequirementTag text={skill.name} />
                  </li>
                </For>
              </ul>
            }
          />
        </If>

        <If condition={languages?.length}>
          <DescriptionItem
            term="Languages proficient in"
            details={
              <ul>
                <For each="language" of={languages} index="index">
                  <Choose>
                    <When condition={index === languages.length - 1}>
                      <li key={language.iso6391Code}>{language.name}</li>
                    </When>
                    <Otherwise>
                      <li key={language.iso6391Code}>{language.name},</li>
                    </Otherwise>
                  </Choose>
                </For>
              </ul>
            }
          />
        </If>
      </DescriptionList>
    </section>
  )
}

SkillsAndExperienceSection.propTypes = {
  roleExperience: PropTypes.shape({
    role: PropTypes.shape({
      name: PropTypes.string,
    }),
    yearsExperience: PropTypes.number,
  }),
  otherRoles: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, id: PropTypes.string })),
  mustHaveSkillExperiences: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      skill: PropTypes.shape({ name: PropTypes.string }),
      yearsExperience: PropTypes.number,
    }),
  ),
  niceToHaveSkills: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, id: PropTypes.string })),
  languages: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, iso6391Code: PropTypes.string })),
}

export default SkillsAndExperienceSection
